import { createEntityAdapter, createSlice, createSelector, Dictionary, EntityId } from '@reduxjs/toolkit';
import { Position, PositionStatus } from './types';
// eslint-disable-next-line import/no-cycle
import { AppState, setOne } from './index';
import {
  activatePosition,
  callPosition,
  cancelPositionByAdmin,
  checkInPosition,
  completePosition,
  createPosition,
  loadClickAndCollect,
  loadPosition,
  loadPositions,
  nextPosition,
  updateNotificationForPosition,
  cancelPositionByUser,
  updatePositionNumberOfPeople,
  createDigitalCallPosition,
  loadDigitalCallPosition,
  submitFeedbackForm,
  assignManagerForBooking,
  assignManagerForPosition,
  unAssignManagerFromPosition,
  removeDigitalCallPositionFromStore,
  checkOutOldestPosition,
  checkOutPosition,
  rejectPosition,
  retryPosition,
  loadPositionBySession,
  createClickAndCollectOrder,
  getClickAndCollectOrders,
  verifyBookingOtp,
} from './api';

const positionsAdapter = createEntityAdapter<Position>();

const setOnePosition = (state: any, item: Position) => {
  return setOne<Position>(positionsAdapter, state, item, item.id);
};

const replacePositions = (state: any, action: { payload: { positions?: { [id: string]: Position } } }) => {
  if (action.payload.positions) {
    Object.entries(action.payload.positions).forEach(([, item]) => {
      setOnePosition(state, item as Position);
    });
  }
};

export const positions = createSlice({
  name: 'positions',
  initialState: positionsAdapter.getInitialState(),
  reducers: {},
  extraReducers: {
    [loadPositions.pending.type]: (state) => ({
      ...state,
    }),
    [loadPositions.fulfilled.type]: (
      state,
      action: { payload: { data: { positions: undefined | { [id: string]: Position } }; isReplaceExisting: boolean } },
    ) => {
      // if it's not socket - we just got all positions and need to sync them as they are
      const incomingPositions = action.payload.data.positions;
      if (action.payload.isReplaceExisting) {
        // if there is no positions - no tickets in the queue
        // A socket message can land while the fetch is in flight, so keep any stored ticket that is
        // newer than the fetched copy. Tickets missing from the fetch are gone and are dropped.
        const positionsList = Object.values<Position>(incomingPositions ?? {}).map((position) => {
          const stored = state.entities[position.id];
          // eslint-disable-next-line no-underscore-dangle
          return stored && stored._ts && position._ts && stored._ts > position._ts ? stored : position;
        });
        positionsAdapter.setAll(state, positionsList);
        return;
      }
      // if it's socket - we should update positions which we got but not the rest
      if (!incomingPositions) return;

      // One pass, O(incoming). The previous merge removed and re-added every incoming
      // ticket, and removing one rebuilds the whole `ids` array, so a message with
      // k tickets cost k x (tickets in the queue): ~0.7 s per 100 tickets at 5,000.
      // A replaced ticket keeps its place in `ids`; nothing depends on that order
      // (the manager sorts by creation date) and it no longer reshuffles per update.
      Object.values<Position>(incomingPositions).forEach((position) => {
        const currentPosition = state.entities[position.id];

        if (!currentPosition) {
          state.ids.push(position.id);
          state.entities[position.id] = position;
          return;
        }

        // keep a newer stored version over an older incoming one
        // eslint-disable-next-line no-underscore-dangle
        if (position._ts && currentPosition._ts && position._ts < currentPosition._ts) return;

        state.entities[position.id] = position;
      });
    },
    [loadClickAndCollect.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [loadPosition.fulfilled.type]: replacePositions,
    // the full position replaces (not merges into) the masked one, so
    // verificationRequired disappears together with the mask
    [verifyBookingOtp.fulfilled.type]: replacePositions,
    [activatePosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [completePosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [rejectPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [cancelPositionByAdmin.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.removeMany(state, Object.keys(action.payload.positions));
      }
    },
    [createPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [createDigitalCallPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        const firstItemKey = Object.keys(action.payload.positions)[0];
        positionsAdapter.upsertOne(state, action.payload.positions[firstItemKey]);
      }
    },
    [nextPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [callPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [retryPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        const firstItemKey = Object.keys(action.payload.positions)[0];
        const firstPosition = action.payload.positions[firstItemKey];
        positionsAdapter.updateOne(state, {
          id: firstItemKey,
          changes: { ...firstPosition, servedBy: firstPosition.servedBy ?? undefined },
        });
      }
    },
    [updateNotificationForPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [checkInPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [checkOutOldestPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [cancelPositionByUser.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [updatePositionNumberOfPeople.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [checkOutPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [loadDigitalCallPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [removeDigitalCallPositionFromStore.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.removeOne(state, Object.keys(action.payload.positions)[0]);
      }
    },
    [submitFeedbackForm.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [assignManagerForBooking.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [assignManagerForPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [unAssignManagerFromPosition.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        const firstPositionId = Object.keys(action.payload.positions)[0];
        const firstPosition = action.payload.positions[firstPositionId];

        positionsAdapter.updateOne(state, { id: firstPositionId, changes: { ...firstPosition, servedBy: undefined } });
      }
    },
    [loadPositionBySession.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [createClickAndCollectOrder.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
    [getClickAndCollectOrders.fulfilled.type]: (state, action) => {
      if (action.payload.positions) {
        positionsAdapter.upsertMany(state, action.payload.positions);
      }
    },
  },
});

export const { selectById: selectPositionById } = positionsAdapter.getSelectors();

export const selectClickAndCollectPositions = (state: AppState, queue: string) =>
  (state.positions.ids as string[]).reduce((acc, id: string) => {
    const position = state.positions.entities[id];
    if (
      position &&
      position.queue === queue &&
      (position.type === 'storePickup' || position.type === 'curbsidePickup')
    ) {
      acc.push(position);
    }
    return acc;
  }, [] as Position[]);

const positionsIdsSelector = (state: AppState) => state.positions.ids;
const positionsEntitiesSelector = (state: AppState) => state.positions.entities;

// One pass without copying the accumulator per element (the previous version spread
// the array for every ticket, O(n^2) in the number of tickets).
const positionsCombiner = (positionStatus: PositionStatus) => (ids: EntityId[], entities: Dictionary<Position>) => {
  const result: Position[] = [];

  ids.forEach((id) => {
    const position = entities[id];

    if (position && position.status === positionStatus) {
      result.push(position);
    }
  });

  return result;
};

export const selectPendingPositions = createSelector(
  positionsIdsSelector,
  positionsEntitiesSelector,
  positionsCombiner(PositionStatus.Pending),
);

export const selectFulfilledPositions = createSelector(
  positionsIdsSelector,
  positionsEntitiesSelector,
  positionsCombiner(PositionStatus.Fulfilled),
);

export const selectNotifiedPositions = createSelector(
  positionsIdsSelector,
  positionsEntitiesSelector,
  positionsCombiner(PositionStatus.Notified),
);

export const selectOrderPositions = createSelector(
  positionsIdsSelector,
  positionsEntitiesSelector,
  positionsCombiner(PositionStatus.Preorder),
);

export default positions.reducer;
